/**
 * Perfil de base del servilletero de paredes.
 *
 * El perfil es un rectángulo en el lienzo: la franja de la pared que queda por
 * debajo de su borde superior es el macho que entra en la base. Se puede mover
 * y estirar con los tiradores; aquí se añade, se ajusta al borde inferior del
 * fondo o se quita. Como los marcadores de la caja de luz, vive en el lienzo y
 * se relee del editor, sin copia en el estado de React.
 */
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { InputSpinner } from '../../../../../../components/ui';
import { useTools } from '../../../../ToolProvider.jsx';

const formatMm = (value) => `${Math.round(value * 10) / 10} mm`;

function NapkinJointPanel({ defaultHeightMm = 8 }) {
    const { getNapkinJoint, onAddNapkinJoint, onFitNapkinJoint, onRemoveNapkinJoint } = useTools();
    const [joint, setJoint] = useState(null);
    const [heightMm, setHeightMm] = useState(defaultHeightMm);
    const refresh = () => setJoint(getNapkinJoint?.() ?? null);

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const after = (action) => () => {
        action();
        setTimeout(refresh, 30);
    };

    return (
        <div className="editor-container space-y-3">
            <div className="flex items-center justify-between gap-2">
                <span className="text-sm">
                    {joint
                        ? `Perfil de ${formatMm(joint.widthMm)} × ${formatMm(joint.heightMm)}`
                        : 'Sin perfil: el macho es la banda inferior por defecto'}
                </span>
                {!joint && (
                    <button
                        type="button"
                        className="btn btn-xs btn-primary"
                        onClick={after(() => onAddNapkinJoint?.({ heightMm }))}
                    >
                        Añadir
                    </button>
                )}
            </div>

            <InputSpinner
                label="Alto del macho (mm)"
                value={heightMm}
                min={4}
                max={20}
                initialStep={0.5}
                decimals={1}
                onChange={(next) => setHeightMm(Number(next))}
            />

            {joint && (
                <div className="grid grid-cols-2 gap-2">
                    <button
                        type="button"
                        className="btn btn-xs btn-ghost"
                        onClick={after(() => onFitNapkinJoint?.({ heightMm }))}
                    >
                        Ajustar al borde inferior
                    </button>
                    <button
                        type="button"
                        className="btn btn-xs btn-ghost text-error"
                        onClick={after(() => onRemoveNapkinJoint?.())}
                    >
                        Quitar
                    </button>
                </div>
            )}

            <div className="rounded-lg border border-info/30 bg-info/10 px-2 py-1 text-[11px] leading-snug text-base-content/70">
                El borde superior del perfil es la <strong>línea de la base</strong>, donde apoya la
                servilleta; lo que queda por debajo entra en la base y se rellena macizo a todo lo
                ancho del perfil, aunque la silueta tenga huecos ahí. Los dibujos se cortan solos un
                margen por encima de esa línea (zona rayada; el margen se cambia en «Unión con la
                base» de los parámetros) y la pared queda lisa ahí. Si el perfil es más estrecho
                que la pared, el resto de la pared apoya sobre la base. La silueta discontinua es
                la servilleta doblada, a escala.
            </div>
        </div>
    );
}

NapkinJointPanel.propTypes = {
    defaultHeightMm: PropTypes.number,
};

export default NapkinJointPanel;
